import React, { useLayoutEffect, useRef, useState } from 'react'
import { getPrefersReducedMotion } from '@/lib/prefersReducedMotion'
import { cn } from '@/lib/utils'
import { formatReasoningForDisplay } from './agentChatUtils'

const WORD_DELAY_MS = 40

type StreamingReasoningTextProps = {
    segmentId: string
    content: string
    enabled: boolean
    className?: string
    onProgress?: () => void
}

function tokenize(text: string): string[] {
    return text.match(/\S+\s*/g) ?? (text ? [text] : [])
}

export const StreamingReasoningText: React.FC<StreamingReasoningTextProps> = ({
    segmentId,
    content,
    enabled,
    className,
    onProgress,
}) => {
    const displayContent = formatReasoningForDisplay(content)
    const [displayedContent, setDisplayedContent] = useState(
        enabled ? '' : displayContent,
    )
    const onProgressRef = useRef(onProgress)
    const activeSegmentIdRef = useRef<string | null>(null)
    const displayedTokenCountRef = useRef(0)
    const contentRef = useRef(displayContent)
    const timeoutIdRef = useRef<ReturnType<typeof setTimeout>>()
    contentRef.current = displayContent
    onProgressRef.current = onProgress

    useLayoutEffect(() => {
        const clearTimer = () => {
            if (timeoutIdRef.current) {
                clearTimeout(timeoutIdRef.current)
                timeoutIdRef.current = undefined
            }
        }

        const revealAll = (text: string) => {
            const tokens = tokenize(text)
            displayedTokenCountRef.current = tokens.length
            setDisplayedContent(text)
        }

        if (!enabled) {
            clearTimer()
            revealAll(contentRef.current)
            return
        }

        if (activeSegmentIdRef.current !== segmentId) {
            activeSegmentIdRef.current = segmentId
            displayedTokenCountRef.current = 0
        }

        if (getPrefersReducedMotion()) {
            clearTimer()
            revealAll(displayContent)
            return
        }

        let disposed = false

        const tick = () => {
            if (disposed) {
                return
            }

            const tokens = tokenize(contentRef.current)
            const revealedCount = displayedTokenCountRef.current

            if (revealedCount >= tokens.length) {
                setDisplayedContent(contentRef.current)
                return
            }

            displayedTokenCountRef.current = revealedCount + 1
            setDisplayedContent(tokens.slice(0, revealedCount + 1).join(''))
            onProgressRef.current?.()
            timeoutIdRef.current = setTimeout(tick, WORD_DELAY_MS)
        }

        const tokens = tokenize(contentRef.current)
        if (displayedTokenCountRef.current === 0 && tokens.length > 0) {
            displayedTokenCountRef.current = 1
            setDisplayedContent(tokens[0] ?? '')
        } else if (displayedTokenCountRef.current > 0) {
            setDisplayedContent(
                tokens.slice(0, displayedTokenCountRef.current).join(''),
            )
        }

        if (displayedTokenCountRef.current < tokens.length) {
            clearTimer()
            timeoutIdRef.current = setTimeout(tick, WORD_DELAY_MS)
        }

        return () => {
            disposed = true
            clearTimer()
        }
    }, [enabled, segmentId, displayContent])

    const isCatchingUp =
        enabled && displayedContent.length < contentRef.current.length

    return (
        <span className={cn(className)}>
            {enabled ? displayedContent : displayContent}
            {isCatchingUp ? (
                <span
                    aria-hidden
                    className="bg-muted-foreground/50 ml-0.5 inline-block h-[1em] w-0.5 animate-pulse align-text-bottom"
                />
            ) : null}
        </span>
    )
}
